<template>
  <div
    v-if="visible"
    class="modal fade show"
    id="controllerConnectionDialog"
    tabindex="-1"
    aria-modal="true"
    style="display: block; background-color: rgba(0, 0, 0, 0.5)"
    @click.stop="onBackClick"
  >
    <div
      class="modal-dialog modal-dialog-centered"
      style="max-width: 500px; width: calc(100% - 2rem); max-height: 90vh; min-height: 600px; margin: auto"
      @click.stop="() => {}"
    >
      <div class="modal-content" style="border-radius: 1.2rem; overflow: hidden; max-height: 90vh; display: flex; flex-direction: column; margin: auto">
        <div class="p-4 dialog-body">
          <!-- 헤더 -->
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h5 class="mb-0 fw-bold">{{ title || '조명제어기 연결' }}</h5>
            <button
              type="button"
              class="btn-close"
              aria-label="Close"
              @click.stop="onCancel"
              :disabled="isProcessing"
            ></button>
          </div>

          <!-- 로컬 로딩 오버레이 (바디 영역만) -->
          <div
            v-if="localLoadingVisible"
            class="local-loading-overlay"
            aria-live="polite"
            aria-busy="true"
          >
            <div class="local-loading-card">
              <div class="d-flex align-items-center gap-2">
                <div class="spinner-border spinner-border-sm text-light" role="status"></div>
                <div class="local-loading-text" v-html="formattedLocalLoadingText"></div>
              </div>
            </div>
          </div>

          <!-- BLE/WiFi 선택 팝업 (상단에 표시) -->
          <div v-if="showConnectionTypeSelection" class="mb-4">
            <div class="card" style="background-color: #f8f9fa; border: 2px solid #0d6efd">
              <div class="card-body p-3">
                <h6 class="mb-3 fw-bold text-center">연결 방식을 선택하세요</h6>
                <div class="d-flex flex-row gap-2 connection-type-container">
                  <button
                    type="button"
                    class="btn btn-outline-primary text-center connection-type-btn"
                    @click="selectConnectionType('ble')"
                    :disabled="isProcessing"
                  >
                    <div class="d-flex flex-column align-items-center h-100 justify-content-center">
                      <i class="fa-brands fa-bluetooth-b mb-2" style="font-size: 1.5rem"></i>
                      <div class="fw-bold">블루투스 (BLE)</div>
                      <div class="small text-muted">지원 모델: {{ CONTROLLER_MODELS.BLE_SUPPORTED.map((m) => m.model).join(', ') }}</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-success text-center connection-type-btn"
                    @click="selectConnectionType('wifi')"
                    :disabled="isProcessing"
                  >
                    <div class="d-flex flex-column align-items-center h-100 justify-content-center">
                      <i class="fa-solid fa-wifi mb-2" style="font-size: 1.5rem"></i>
                      <div class="fw-bold">WiFi (AP 모드)</div>
                      <div class="small text-muted">지원 모델: {{ CONTROLLER_MODELS.WIFI_SUPPORTED.map((m) => m.model).join(', ') }}</div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- 진행 단계 표시 (연결 방식 선택 후에만 표시) -->
          <div v-if="!showConnectionTypeSelection" class="mb-4">
            <!-- 연결 방식 정보 (스캔 단계 위에 표시) -->
            <div v-if="connectionType" class="mb-3 ms-5">
              <p class="text-muted small mb-0">
                연결 방식: {{ connectionType === 'ble' ? '블루투스 (BLE)' : 'WiFi' }}
              </p>
            </div>
            <div class="step-container">
              <div
                v-for="(step, index) in connectionSteps"
                :key="index"
                class="step-item"
              >
                <div class="step-content">
                  <div
                    class="step-indicator"
                    :class="{
                      'step-pending': step.status === 'pending',
                      'step-in-progress': step.status === 'in_progress',
                      'step-completed': step.status === 'completed',
                      'step-failed': step.status === 'failed',
                    }"
                  >
                    <div class="step-number">
                      <span v-if="step.status === 'completed'">✓</span>
                      <span v-else-if="step.status === 'failed'">✗</span>
                      <span v-else-if="step.status === 'in_progress'">
                        <span
                          class="spinner-border spinner-border-sm"
                          role="status"
                        ></span>
                      </span>
                      <span v-else>{{ index + 1 }}</span>
                    </div>
                  </div>
                  <div class="step-label">
                    <div class="fw-bold">{{ step.title }}</div>
                    <div
                      v-if="step.status === 'in_progress' && step.message"
                      class="text-muted mt-1"
                      style="font-size: 0.75rem"
                    >
                      {{ step.message }}
                    </div>
                    <div
                      v-if="step.status === 'completed' && step.lastValue"
                      class="text-success mt-1"
                      style="font-size: 0.75rem"
                    >
                      {{ step.lastValue }}
                    </div>
                    <div
                      v-if="step.status === 'failed' && step.error"
                      class="text-danger mt-1"
                      style="font-size: 0.75rem"
                    >
                      {{ step.error }}
                    </div>
                    <div
                      v-if="step.status === 'failed' && step.lastValue"
                      class="text-danger mt-1"
                      style="font-size: 0.75rem"
                    >
                      {{ step.lastValue }}
                    </div>
                  </div>
                </div>
                
                <!-- 단계별 컨텐츠 영역 -->
                <div v-if="step.status === 'completed' || step.status === 'in_progress' || step.status === 'failed' || (index === 2 && step.status === 'pending' && showWifiInput) || (index === 2 && step.status === 'completed' && wifiSsid && wifiPassword)" class="step-content-area mt-1 ms-5">
                  <!-- 스캔 단계: 스캔된 단말 정보 -->
                  <div v-if="index === 0 && step.status === 'completed'" class="step-details">
                    <div class="card" style="background-color: #f8f9fa">
                      <div class="card-body p-3">
                        <div class="small">
                          <div><strong>{{ step.lastValue }}</strong></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- 연결/프로비저닝 단계: 컨트롤러 정보 -->
                  <div v-if="index === 1 && (step.status === 'completed' || step.status === 'in_progress' || step.status === 'failed')" class="step-details">
                    <div v-if="connectedController._id" class="card" style="background-color: #f8f9fa">
                      <div class="card-body p-3">
                        <div class="small">
                          <div><strong>시리얼 넘버:</strong> {{ connectedController._id }}</div>
                          <div><strong>모델 코드:</strong> {{ connectedController.model }}</div>
                          <div><strong>펌웨어 버전:</strong> {{ connectedController.version }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- 설정 단계: WiFi 입력, 설정 버튼 -->
                  <div v-if="index === 2" class="step-details">
                    <!-- WiFi 입력창 (pending, in_progress, failed 상태일 때) -->
                    <div v-if="showWifiInput && (step.status === 'pending' || step.status === 'in_progress' || step.status === 'failed')" class="mb-3">
                      <div class="mb-2">
                        <label class="form-label small">WiFi SSID</label>
                        <input
                          type="text"
                          class="form-control form-control-sm"
                          :placeholder="wifiSsidPlaceholder"
                          v-model="wifiSsid"
                          :disabled="isProcessing"
                        />
                      </div>
                      <div class="mb-3">
                        <label class="form-label small">WiFi 비밀번호</label>
                        <div class="input-group">
                          <input
                            :type="showPassword ? 'text' : 'password'"
                            class="form-control form-control-sm"
                            :placeholder="wifiPasswordPlaceholder"
                            v-model="wifiPassword"
                            :disabled="isProcessing"
                          />
                          <button
                            type="button"
                            class="btn btn-outline-secondary btn-sm"
                            @click="showPassword = !showPassword"
                            :disabled="isProcessing"
                          >
                            <i :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
                          </button>
                        </div>
                      </div>
                      <button
                        v-if="connectionSteps[2].status === 'pending' || connectionSteps[2].status === 'failed'"
                        type="button"
                        class="btn btn-primary w-100"
                        @click="onWifiSetting"
                        :disabled="isProcessing || !canProceedWifiSetting"
                      >
                        설정
                      </button>
                    </div>
                    <!-- WiFi 설정 정보 표시 (completed 상태일 때) -->
                    <div v-if="step.status === 'completed' && wifiSsid && wifiPassword" class="mb-3">
                      <div class="card" style="background-color: #f8f9fa">
                        <div class="card-body p-3">
                          <div class="small">
                            <div class="mb-2">
                              <strong>WiFi SSID:</strong> {{ wifiSsid }}
                            </div>
                            <div class="d-flex align-items-center gap-2">
                              <div class="flex-grow-1">
                                <strong>WiFi 비밀번호:</strong> {{ showPassword ? wifiPassword : '•'.repeat(wifiPassword.length || 8) }}
                              </div>
                              <button
                                type="button"
                                class="btn btn-sm btn-outline-secondary"
                                @click="showPassword = !showPassword"
                                style="padding: 0.25rem 0.5rem; font-size: 0.75rem"
                              >
                                <i :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div
                  v-if="index < connectionSteps.length - 1"
                  class="step-connector"
                  :class="{
                    'connector-active': step.status === 'completed',
                  }"
                ></div>
              </div>
            </div>
          </div>


          <!-- 에러 메시지 -->
          <div v-if="errorMessage" class="alert alert-danger small mb-3">
            {{ errorMessage }}
          </div>
        </div>
        
        <!-- 푸터 버튼 (하단 고정) -->
        <div class="p-4 pt-0" style="background-color: white; flex-shrink: 0">
          <div class="d-flex gap-2">
            <button
              v-if="showCancel"
              type="button"
              class="btn btn-secondary flex-fill"
              @click.stop="onCancel"
              :disabled="isProcessing && !hasFailedStep"
            >
              {{ cancelText }}
            </button>
            <button
              v-if="!showConnectionTypeSelection && (hasFailedStep || connectionSteps[2].status === 'completed')"
              type="button"
              class="btn btn-primary flex-fill"
              @click.stop="onOk"
              :disabled="isProcessing && !hasFailedStep || (connectionSteps[2].status !== 'completed' && !hasFailedStep)"
            >
              {{ getOkText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  nextTick,
  getCurrentInstance,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import useControllerProvisioning from '@/common/hooks/useControllerProvisioning';
import useMobile from '@/common/hooks/useMobile';
import { DeviceDefault, CONTROLLER_MODELS, SettingConfig } from '@/common/constants';
import {
  loadSavedWifiCredentials,
  saveWifiCredentials,
} from '@/common/utils/savedWifiCredentials';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '조명제어기 연결',
  },
  okText: {
    type: String,
    default: '확인',
  },
  cancelText: {
    type: String,
    default: '취소',
  },
  showCancel: {
    type: Boolean,
    default: true,
  },
  needsProvisioning: {
    type: Boolean,
    default: true, // WiFi/MQTT 설정 필요 여부
  },
  needsWifiInput: {
    type: Boolean,
    default: true, // WiFi 정보 입력 필요 여부
  },
  initialWifiSsid: {
    type: String,
    default: '',
  },
  initialWifiPassword: {
    type: String,
    default: '',
  },
  actionType: {
    type: String,
    default: null, // 'register' | 'replace' | 'transfer' | 'wifi-change' | null
  },
  defaultConnectionType: {
    type: String,
    default: null, // null: BLE/WiFi 선택 | 'ble' | 'wifi': 해당 방식으로 바로 진입
  },
});

const emit = defineEmits(['complete', 'cancel', 'error']);

const { emitter, mobile, logger } =
  getCurrentInstance().appContext.config.globalProperties;

const {
  connectWifiAp,
  provisionOverWifiAp,
  checkBleAdapter,
  scanBleWithRetry,
  disconnectBleSafe,
  provisionOverBle,
  normalizeControllerInfoFromSerial,
} = useControllerProvisioning();

// useMobile hook을 최상위에서 호출 (서버 등록 대기 시 getControllerInfo 사용)
const { connectBleDevice: connectBleDeviceFromMobile, getControllerInfo } = useMobile();

// 상태 관리
const isProcessing = ref(false);
const connectionType = ref(null); // 'ble' | 'wifi' | null
const isBleConnected = ref(false);
const showConnectionTypeSelection = ref(false); // BLE/WiFi 선택 팝업 표시 여부
const connectedController = ref({
  _id: '',
  version: '',
  model: '',
});
const wifiSsid = ref(props.initialWifiSsid || '');
const wifiPassword = ref(props.initialWifiPassword || '');
/** 저장된 자격증명(입력란이 비어 있을 때 placeholder로 안내하고 제출 시 사용) */
const savedWifiSsid = ref('');
const savedWifiPassword = ref('');
const errorMessage = ref('');
const showWifiInput = ref(false);
const showPassword = ref(false);

const applySavedWifiDefaults = () => {
  const saved = loadSavedWifiCredentials();
  savedWifiSsid.value = saved.ssid || '';
  savedWifiPassword.value = saved.password || '';

  if (props.initialWifiSsid) {
    wifiSsid.value = props.initialWifiSsid;
  } else if (saved.ssid) {
    wifiSsid.value = saved.ssid;
  } else {
    wifiSsid.value = '';
  }

  if (props.initialWifiPassword) {
    wifiPassword.value = props.initialWifiPassword;
  } else {
    // 저장된 비번이 있으면 입력란은 비우고 placeholder ***** 로 표시
    wifiPassword.value = '';
  }
};

const resolveWifiCredentials = () => {
  // SSID는 입력란 값 사용(열 때 저장된 값으로 미리 채움). 사용자가 지우면 빈 값.
  const ssid = `${wifiSsid.value || ''}`.trim();
  const typedPassword = wifiPassword.value;
  // 비밀번호는 비어 있으면 저장된 값(***** placeholder) 사용
  const password =
    typedPassword && typedPassword.length > 0
      ? typedPassword
      : savedWifiPassword.value || '';
  return { ssid, password };
};

const wifiSsidPlaceholder = computed(() => {
  if (wifiSsid.value) {
    return 'ssid 입력해주세요.';
  }
  return savedWifiSsid.value || 'ssid 입력해주세요.';
});

const wifiPasswordPlaceholder = computed(() => {
  if (savedWifiPassword.value && !(wifiPassword.value && wifiPassword.value.length > 0)) {
    return '*****';
  }
  return 'password 입력해주세요.';
});

const hasWifiCredentialsReady = computed(() => {
  const { ssid, password } = resolveWifiCredentials();
  return ssid.length > 0 && password.length > 0;
});

const rememberWifiCredentialsIfReady = () => {
  const { ssid, password } = resolveWifiCredentials();
  if (ssid && password) {
    saveWifiCredentials(ssid, password);
    savedWifiSsid.value = ssid;
    savedWifiPassword.value = password;
    wifiSsid.value = ssid;
    // 성공 후 요약 표시용으로 실제 비밀번호를 입력란에도 반영
    wifiPassword.value = password;
  }
  return { ssid, password };
};

// 로컬 로딩(모달 내부) 상태
const localLoadingVisible = ref(false);
const localLoadingText = ref('');
const formattedLocalLoadingText = computed(() => {
  return `${localLoadingText.value || ''}`.replace(/\n/g, '<br>');
});

// 연결 취소 플래그: 다이얼로그 닫기/취소 시 true → 스캔·연결 완료 콜백에서 로직 진행 중단
const connectionAborted = ref(false);

// BLE 연결 재시도 관련 변수
const scannedDevice = ref(null); // 스캔된 장치 정보 저장
const MAX_CONNECTION_RETRIES = 5; // 최대 연결 재시도 횟수

// WiFi(AP) 연결 재시도 (BLE 공통화 이전 ControllerSettingPage.vue reConnect()와 동일)
const MAX_WIFI_CONNECT_RETRIES = 5;
const WIFI_CONNECT_RETRY_DELAY_MS = 5000;

// 연결 단계 상태 관리 (computed로 변경하여 actionType에 따라 동적으로 단계 수 변경)
const connectionStepsBase = ref([
  {
    title: '스캔',
    status: 'pending', // pending, in_progress, completed, failed
    message: '',
    error: '',
    lastValue: '', // 스캔된 단말 시리얼
  },
  {
    title: '연결/프로비저닝',
    status: 'pending',
    message: '',
    error: '',
    lastValue: '', // 연결 성공 → 프로비저닝 성공
  },
  {
    title: '설정',
    status: 'pending',
    message: '',
    error: '',
    lastValue: '', // 설정 성공 여부
  },
]);

// 4번째 단계 상태 관리 (actionType이 있을 때만 사용)
const fourthStep = ref({
  title: '완료',
  status: 'pending',
  message: '',
  error: '',
  lastValue: '',
});

const connectionSteps = computed(() => {
  const steps = [...connectionStepsBase.value];
  
  // actionType이 있으면 4번째 단계 추가
  if (props.actionType) {
    let stepTitle = '완료';
    switch (props.actionType) {
      case 'register':
        stepTitle = '등록';
        break;
      case 'replace':
        stepTitle = '교체';
        break;
      case 'transfer':
        stepTitle = '이관';
        break;
      case 'wifi-change':
        stepTitle = '완료';
        break;
    }
    steps.push({ 
      ...fourthStep.value,
      title: stepTitle,
    });
  }
  
  return steps;
});

const updateStepStatus = (stepIndex, status, message = '', error = '', lastValue = '') => {
  if (stepIndex >= 0 && stepIndex < connectionStepsBase.value.length) {
    connectionStepsBase.value[stepIndex].status = status;
    connectionStepsBase.value[stepIndex].message = message;
    connectionStepsBase.value[stepIndex].error = error;
    if (lastValue !== '') {
      connectionStepsBase.value[stepIndex].lastValue = lastValue;
    }
  }
  // 4번째 단계가 있는 경우 (actionType이 있는 경우)
  if (props.actionType && stepIndex === 3) {
    fourthStep.value.status = status;
    fourthStep.value.message = message;
    fourthStep.value.error = error;
    if (lastValue !== '') {
      fourthStep.value.lastValue = lastValue;
    }
  }
};

const resetSteps = () => {
  connectionStepsBase.value.forEach((step) => {
    step.status = 'pending';
    step.message = '';
    step.error = '';
    step.lastValue = '';
  });
  // 4번째 단계도 초기화
  if (props.actionType) {
    fourthStep.value.status = 'pending';
    fourthStep.value.message = '';
    fourthStep.value.error = '';
    fourthStep.value.lastValue = '';
  }
};

const canProceed = computed(() => {
  // 연결 방식 선택이 필요한 경우
  if (showConnectionTypeSelection.value) {
    return false;
  }
  
  // 연결 실패 상태에서는 재연결 가능
  if (hasFailedStep.value) {
    return true;
  }
  
  // 스캔이 완료되지 않았으면 비활성화
  if (connectionSteps.value[0].status !== 'completed') {
    return false;
  }
  
  // 연결/프로비저닝 단계
  if (connectionSteps.value[1].status === 'completed') {
    // 프로비저닝이 필요하고 WiFi 입력이 필요하면
    if (props.needsProvisioning && props.needsWifiInput) {
      // WiFi 입력이 있으면 활성화 (프로비저닝 실행 가능)
      return hasWifiCredentialsReady.value;
    }
    // 프로비저닝이 필요 없으면 설정 단계 완료 시 활성화
    if (!props.needsProvisioning) {
      return connectionSteps.value[2].status === 'completed';
    }
    // 프로비저닝이 필요하지만 WiFi 입력이 필요 없으면 설정 단계 완료 시 활성화
    return connectionSteps.value[2].status === 'completed';
  }
  
  // 연결/프로비저닝이 완료되지 않았고 WiFi 입력이 필요한 경우
  if (props.needsProvisioning && props.needsWifiInput) {
    // WiFi 입력이 있으면 활성화 (프로비저닝 실행 가능)
    return hasWifiCredentialsReady.value;
  }
  
  // 그 외의 경우 비활성화
  return false;
});

const hasFailedStep = computed(() => {
  return connectionSteps.value.some(step => step.status === 'failed');
});

const canProceedWifiSetting = computed(() => {
  return hasWifiCredentialsReady.value;
});

const getOkText = computed(() => {
  // 연결 방식 선택이 필요한 경우
  if (showConnectionTypeSelection.value) {
    return '선택';
  }

  // 연결 실패 상태 확인
  if (hasFailedStep.value) {
    return '재연결';
  }

  // 스캔이 완료되지 않았으면 연결 시작
  if (connectionSteps.value[0].status !== 'completed') {
    return '연결 시작';
  }

  // 연결/프로비저닝 단계가 완료되었지만 설정 단계가 완료되지 않은 경우
  if (connectionSteps.value[1].status === 'completed' && connectionSteps.value[2].status !== 'completed') {
    // 프로비저닝이 필요하면 "설정" (프로비저닝 실행)
    if (props.needsProvisioning) {
      return '설정';
    }
    // 프로비저닝이 필요 없으면 okText prop 사용 (기본값: '등록')
    return props.okText;
  }

  // 연결/프로비저닝이 완료되지 않았으면 설정 (프로비저닝 실행)
  if (connectionSteps.value[1].status !== 'completed') {
    return '설정';
  }

  // 설정 완료 시 okText prop 사용 (기본값: '등록', 교체 페이지에서는 '교체')
  return props.okText;
});

// 팝업이 열릴 때 선택 팝업 표시
watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      connectionAborted.value = false;
      isProcessing.value = false;
      // body 스크롤 방지
      document.body.style.overflow = 'hidden';
      resetSteps();
      connectionType.value = null;
      isBleConnected.value = false;
      scannedDevice.value = null; // 스캔된 장치 정보 초기화
      connectedController.value = {
        _id: '',
        version: '',
        model: '',
      };
      errorMessage.value = '';
      showWifiInput.value = false;
      if (props.defaultConnectionType === 'ble' || props.defaultConnectionType === 'wifi') {
        showConnectionTypeSelection.value = false;
        connectionType.value = props.defaultConnectionType;
        nextTick(() => {
          startConnection();
        });
      } else {
        showConnectionTypeSelection.value = true; // 선택 팝업 표시
      }
      wifiSsid.value = props.initialWifiSsid || '';
      wifiPassword.value = props.initialWifiPassword || '';
      applySavedWifiDefaults();
      // 4번째 단계 초기화
      if (props.actionType) {
        fourthStep.value.status = 'pending';
        fourthStep.value.message = '';
        fourthStep.value.error = '';
        fourthStep.value.lastValue = '';
      }
    } else {
      // body 스크롤 복원
      document.body.style.overflow = '';
      // 팝업이 닫힐 때 정리
      cleanup();
      showConnectionTypeSelection.value = false;
      scannedDevice.value = null; // 팝업 닫힐 때 스캔된 장치 정보 초기화
    }
  },
);

const cleanup = async () => {
  connectionAborted.value = true;
  try {
    // 로컬 로딩 정리
    localLoadingVisible.value = false;
    localLoadingText.value = '';

    if (isBleConnected.value) {
      await disconnectBleSafe();
    }
    if (connectionType.value === 'wifi') {
      mobile.disConnectWifi(
        JSON.stringify({
          ssid: DeviceDefault.AP_SSID,
          password: DeviceDefault.AP_PWD,
        }),
      );
    }
  } catch (e) {
    logger.log('Cleanup error:', e);
  }
  isBleConnected.value = false;
  connectionType.value = null;
};

onBeforeUnmount(() => {
  window.removeEventListener('nativeBackRequest', handleNativeBackRequest);
  cleanup();
  document.body.style.overflow = ''; // 스크롤 복원
});

// 연결 방식 선택 (새 연결 시작 시 이전 스캔 결과 초기화)
const selectConnectionType = async (type) => {
  scannedDevice.value = null;
  showConnectionTypeSelection.value = false;
  connectionType.value = type;
  await startConnection();
};

const showLocalLoading = (text = '') => {
  localLoadingVisible.value = true;
  if (typeof text === 'string') {
    localLoadingText.value = text;
  }
};
const hideLocalLoading = () => {
  localLoadingVisible.value = false;
};
const setLocalLoadingText = (text = '') => {
  localLoadingText.value = `${text || ''}`;
};
const appendLocalLoadingText = (text = '') => {
  const t = `${text || ''}`.trim();
  if (!t) return;
  localLoadingText.value = localLoadingText.value
    ? `${localLoadingText.value}\n${t}`
    : t;
};

// 연결 시작
const startConnection = async () => {
  if (!connectionType.value) {
    errorMessage.value = '연결 방식을 선택해주세요.';
    return;
  }

  isProcessing.value = true;
  errorMessage.value = '';

  try {
    if (connectionType.value === 'ble') {
      await tryBleConnection();
    } else if (connectionType.value === 'wifi') {
      await tryWifiConnection();
    }
  } catch (e) {
    logger.log('Connection failed:', e);
    errorMessage.value = e?.message || '연결에 실패했습니다. 다시 시도해주세요.';
    isProcessing.value = false;
  }
};

// BLE 연결 시도
const tryBleConnection = async () => {
  try {
    let device = scannedDevice.value;
    let serialNo = null;

    // 1단계: 스캔 (scannedDevice가 없을 때만 스캔 수행)
    if (!device) {
      showLocalLoading('BLE 상태 확인 중...');
      updateStepStatus(0, 'in_progress', 'BLE 상태 확인 중...');

      try {
        await checkBleAdapter();
      } catch (e) {
        if (connectionAborted.value) return;
        hideLocalLoading();
        updateStepStatus(0, 'failed', '', 'BLE 사용 불가', '스캔 실패: BLE 사용 불가');
        throw new Error('BLE를 사용할 수 없습니다. 블루투스를 켜고 다시 시도해주세요.');
      }
      if (connectionAborted.value) return;

      setLocalLoadingText('BLE 장치 스캔 중...');
      updateStepStatus(0, 'in_progress', 'BLE 장치 스캔 중...');

      let devices = [];
      try {
        devices = await scanBleWithRetry({
          maxRetries: 0,
          timeout: 5,
          filterPrefix: 'inshow_m',
        });
        if (connectionAborted.value) return;
        logger.log('BLE devices found:', devices);
      } catch (e) {
        if (connectionAborted.value) return;
        hideLocalLoading();
        updateStepStatus(0, 'failed', '', 'BLE 스캔 실패', '스캔 실패: BLE 장치를 찾을 수 없습니다');
        throw new Error('BLE 장치를 찾을 수 없습니다. 조명제어기가 셋팅 모드로 되어 있는지 확인해주세요.');
      }

      if (devices.length === 0) {
        hideLocalLoading();
        updateStepStatus(0, 'failed', '', 'BLE 장치 없음', '스캔 실패: BLE 장치 없음');
        throw new Error('BLE 장치를 찾을 수 없습니다. 조명제어기가 셋팅 모드로 되어 있는지 확인해주세요.');
      }

      // 스캔된 장치 정보 추출 및 저장
      device = devices[0];
      // scannedDevice.value = device; // 스캔된 장치 정보 저장

      serialNo = device.name || '';
      if (serialNo.startsWith('InShow_')) {
        serialNo = serialNo.substring('InShow_'.length);
      }

      if (!serialNo) {
        hideLocalLoading();
        scannedDevice.value = null; // 시리얼 번호 추출 실패 시 초기화
        updateStepStatus(0, 'failed', '', '시리얼 번호 추출 실패', '스캔 실패: 시리얼 번호 추출 불가');
        throw new Error('BLE 장치 이름에서 정보를 추출할 수 없습니다.');
      }

      // 스캔 단계 완료
      updateStepStatus(0, 'completed', '', '', `스캔된 단말: ${serialNo}`);
      setLocalLoadingText(`스캔 완료: ${serialNo}`);
    } else {
      // 재연결 시: 저장된 장치 정보 사용
      serialNo = device.name || '';
      if (serialNo.startsWith('InShow_')) {
        serialNo = serialNo.substring('InShow_'.length);
      }
      showLocalLoading(`재연결 시도: ${serialNo}`);
    }
    if (connectionAborted.value) return;

    // 2단계: 연결/프로비저닝 (재시도 로직 포함)
    await new Promise((r) => setTimeout(r, 2000)); // 스캔 후 안정화 대기
    if (connectionAborted.value) return;

    let connectionSuccess = false;
    let lastError = null;

    // 최대 3회 재시도
    for (let retryCount = 0; retryCount < MAX_CONNECTION_RETRIES; retryCount++) {
      if (connectionAborted.value) return;
      try {
        if (retryCount > 0) {
          // 재시도 전 대기
          await new Promise((r) => setTimeout(r, 1000));
          setLocalLoadingText(`BLE 연결 재시도 중 (${retryCount + 1}/${MAX_CONNECTION_RETRIES}): ${device.name || '장치'}`);
          updateStepStatus(1, 'in_progress', `BLE 연결 재시도 중 (${retryCount + 1}/${MAX_CONNECTION_RETRIES})...`);
        } else {
          setLocalLoadingText(`BLE 연결 중: ${device.name || '장치'}`);
          updateStepStatus(1, 'in_progress', 'BLE 연결 중...');
        }

        await connectBleDeviceFromMobile(device.deviceId, null, device.name);
        logger.log('BLE connected successfully');

        const controllerInfo = normalizeControllerInfoFromSerial(serialNo, '0');
        connectedController.value = controllerInfo;
        connectionType.value = 'ble';
        isBleConnected.value = true;
        updateStepStatus(1, 'completed', '', '', '연결 성공');
        hideLocalLoading();
        isProcessing.value = false;
        connectionSuccess = true;

        // 프로비저닝이 필요하면 WiFi 입력 표시
        if (props.needsProvisioning && props.needsWifiInput) {
          scannedDevice.value = null;
          showWifiInput.value = true;
          applySavedWifiDefaults();
          // step[1]은 completed 유지, step[2]는 pending 상태 유지 (WiFi 설정 대기)
          updateStepStatus(1, 'completed', 'WiFi 정보 입력 대기 중...', '', '연결 성공');
          // step[2]는 pending 상태 유지
        } else if (!props.needsProvisioning) {
          // 프로비저닝이 필요 없으면 연결/프로비저닝 단계 완료, 설정 단계도 완료
          updateStepStatus(1, 'completed', '', '', '연결/프로비저닝 성공');
          updateStepStatus(2, 'completed', '', '', '설정 완료');
        }

        break; // 연결 성공 시 루프 종료
      } catch (e) {
        if (connectionAborted.value) return;
        lastError = e;
        logger.log(`BLE connection attempt ${retryCount + 1} failed:`, e);
        
        // 마지막 재시도 실패 시 에러 처리
        if (retryCount === MAX_CONNECTION_RETRIES - 1) {
          hideLocalLoading();
          updateStepStatus(1, 'failed', '', e?.message || '연결 실패', `연결 실패 (${MAX_CONNECTION_RETRIES}회 시도): ${e?.message || '알 수 없는 오류'}`);
          throw e;
        }
      }
    }

    if (connectionAborted.value) return;
    if (!connectionSuccess && lastError) {
      hideLocalLoading();
      updateStepStatus(1, 'failed', '', lastError?.message || '연결 실패', `연결 실패 (${MAX_CONNECTION_RETRIES}회 시도): ${lastError?.message || '알 수 없는 오류'}`);
      throw lastError;
    }
  } catch (e) {
    if (connectionAborted.value) return;
    await disconnectBleSafe();
    isBleConnected.value = false;
    hideLocalLoading();
    throw e;
  }
};

// WiFi 연결 시도 (기존 reConnect 패턴: 최대 5회, 5초 간격)
const tryWifiConnection = async () => {
  try {
    // 1단계: 스캔 (WiFi의 경우 스캔 단계는 건너뛰고 준비 상태로 표시)
    showLocalLoading('WiFi AP 연결 준비 중...');
    updateStepStatus(0, 'in_progress', 'WiFi AP 연결 준비 중...');
    
    // WiFi는 스캔 단계를 건너뛰고 완료로 표시
    updateStepStatus(0, 'completed', '', '', 'WiFi AP 연결 준비 완료');

    // 2단계: 연결/프로비저닝 (최대 5회 재시도, 5초 간격)
    let lastError = null;
    for (let attempt = 0; attempt < MAX_WIFI_CONNECT_RETRIES; attempt++) {
      if (connectionAborted.value) return;
      try {
        if (attempt > 0) {
          await new Promise((r) => setTimeout(r, WIFI_CONNECT_RETRY_DELAY_MS));
          if (connectionAborted.value) return;
          setLocalLoadingText(`조명제어기에 ${attempt + 2}번째 연결 시도하고 있습니다.`);
          updateStepStatus(1, 'in_progress', `WiFi 재시도 (${attempt + 1}/${MAX_WIFI_CONNECT_RETRIES})...`);
        } else {
          setLocalLoadingText('조명제어기에 연결하고 있습니다.');
          updateStepStatus(1, 'in_progress', 'WiFi 연결 중...');
        }

        const { controllerInfo } = await connectWifiAp({
          ssid: DeviceDefault.AP_SSID,
          password: DeviceDefault.AP_PWD,
          onProgress: (msg) => appendLocalLoadingText(msg),
        });
        if (connectionAborted.value) return;

        connectedController.value = controllerInfo;
        connectionType.value = 'wifi';
        isBleConnected.value = false;
        updateStepStatus(1, 'completed', '', '', '연결 성공');
        hideLocalLoading();
        isProcessing.value = false;

        // 프로비저닝이 필요하면 WiFi 입력 표시
        if (props.needsProvisioning && props.needsWifiInput) {
          scannedDevice.value = null;
          showWifiInput.value = true;
          applySavedWifiDefaults();
          updateStepStatus(1, 'completed', 'WiFi 정보 입력 대기 중...', '', '연결 성공');
        } else if (!props.needsProvisioning) {
          updateStepStatus(1, 'completed', '', '', '연결/프로비저닝 성공');
          updateStepStatus(2, 'completed', '', '', '설정 완료');
          if (props.actionType && connectionSteps.value.length > 3) {
            updateStepStatus(3, 'pending', '', '', '');
            setTimeout(() => {
              emit('complete', {
                controller: connectedController.value,
                connectionType: connectionType.value,
                wifiSsid: wifiSsid.value,
                wifiPassword: wifiPassword.value,
              });
            }, 500);
          }
        }
        return;
      } catch (e) {
        if (connectionAborted.value) return;
        lastError = e;
        logger.log(`WiFi connect attempt ${attempt + 1}/${MAX_WIFI_CONNECT_RETRIES} failed:`, e?.message || e);
        if (attempt === MAX_WIFI_CONNECT_RETRIES - 1) {
          throw e;
        }
      }
    }
    if (connectionAborted.value) return;
    if (lastError) {
      throw lastError;
    }
  } catch (e) {
    if (connectionAborted.value) return;
    hideLocalLoading();
    updateStepStatus(1, 'failed', '', e?.message || 'WiFi 연결 실패', `연결 실패 (${MAX_WIFI_CONNECT_RETRIES}회 시도): ${e?.message || '알 수 없는 오류'}`);
    errorMessage.value = e?.message || 'WiFi 연결에 실패했습니다.';
    throw e;
  }
};

// 프로비저닝 실행
const executeProvisioning = async () => {
  if (!props.needsProvisioning) {
    return;
  }

  const wifiCreds = resolveWifiCredentials();
  if (props.needsWifiInput && (!wifiCreds.ssid || !wifiCreds.password)) {
    errorMessage.value = 'WiFi SSID와 비밀번호를 입력해주세요.';
    return;
  }

  try {
    if (connectionAborted.value) return;
    isProcessing.value = true;
    errorMessage.value = '';
    // step[1]은 변경하지 않음 (이미 completed 상태)
    // step[2]를 in_progress로 설정 (설정 중)
    updateStepStatus(2, 'in_progress', '프로비저닝 중...');
    showLocalLoading('프로비저닝 중...');

    if (connectionType.value === 'ble' && isBleConnected.value) {
      setLocalLoadingText('BLE로 WiFi/MQTT 설정을 전송합니다.');
      updateStepStatus(2, 'in_progress', 'BLE로 WiFi/MQTT 설정 전송 중...');

      const provisionResult = await provisionOverBle({
        wifi: {
          ssid: wifiCreds.ssid,
          password: wifiCreds.password,
        },
        mqtt: {
          url: process.env.VUE_APP_CTRL_MQTT_DOMAIN,
          username: '',
          password: '',
        },
        wifiTimeout: 15,
        mqttTimeout: 15,
      });
      if (connectionAborted.value) return;

      logger.log('BLE provision result:', provisionResult);

      if (
        !provisionResult?.wifi ||
        (!provisionResult.wifi.wifi_state && !provisionResult.wifi.connected)
      ) {
        updateStepStatus(2, 'failed', '', 'WiFi 연결 실패', '프로비저닝 실패: WiFi 연결 실패');
        throw new Error('WiFi 연결 실패');
      }

      await disconnectBleSafe();
      isBleConnected.value = false;
      // step[1]은 변경하지 않음, step[2]만 완료 처리
      updateStepStatus(2, 'completed', '', '', '설정 성공');
      hideLocalLoading();
      const savedWifi = rememberWifiCredentialsIfReady();
      
      // actionType이 있으면 4번째 단계를 pending으로 설정하고 자동으로 complete 이벤트 발생
      if (props.actionType && connectionSteps.value.length > 3) {
        updateStepStatus(3, 'pending', '', '', '');
        // 약간의 지연 후 자동으로 complete 이벤트 발생 (사용자가 상태를 확인할 수 있도록)
        await new Promise(r => setTimeout(r, 500));
        emit('complete', {
          controller: connectedController.value,
          connectionType: connectionType.value,
          wifiSsid: savedWifi.ssid,
          wifiPassword: savedWifi.password,
        });
      }
    } else {
      setLocalLoadingText('조명제어기에 와이파이 정보를 설정하고 있습니다.');
      updateStepStatus(2, 'in_progress', 'WiFi 설정 중...');

      // WiFi 프로비저닝 중간 메시지 처리
      const handleSettingResultMsg = (payload) => {
        if (payload?.msg) {
          // 네이티브에서 전송하는 메시지를 단계 상태에 반영
          updateStepStatus(2, 'in_progress', payload.msg);
        }
      };

      emitter.on('onSettingResultMsg', handleSettingResultMsg);

      try {
        await provisionOverWifiAp({
          settingParams: {
            ssid: wifiCreds.ssid,
            pwd: wifiCreds.password,
            mqtt_url: process.env.VUE_APP_CTRL_MQTT_DOMAIN,
            firmware_version: '',
            firmware_url: '',
            uuid: '',
            device_key: '',
            mqtt_username: '',
            mqtt_password: '',
          },
          apWifi: {
            ssid: DeviceDefault.AP_SSID,
            password: DeviceDefault.AP_PWD,
          },
          onProgress: (msg) => appendLocalLoadingText(msg),
        });
        if (connectionAborted.value) return;

        // step[1]은 변경하지 않음, step[2]만 완료 처리
        updateStepStatus(2, 'completed', '', '', '설정 성공');
        hideLocalLoading();
        const savedWifi = rememberWifiCredentialsIfReady();
        
        // actionType이 있으면 4번째 단계를 pending으로 설정하고 자동으로 complete 이벤트 발생
        if (props.actionType && connectionSteps.value.length > 3) {
          updateStepStatus(3, 'pending', '', '', '');
          // 약간의 지연 후 자동으로 complete 이벤트 발생 (사용자가 상태를 확인할 수 있도록)
          await new Promise(r => setTimeout(r, 500));
          emit('complete', {
            controller: connectedController.value,
            connectionType: connectionType.value,
            wifiSsid: savedWifi.ssid,
            wifiPassword: savedWifi.password,
          });
        }
      } finally {
        // 이벤트 리스너 정리
        emitter.off('onSettingResultMsg', handleSettingResultMsg);
      }
    }
  } catch (e) {
    if (connectionAborted.value) return;
    hideLocalLoading();

    // 등록 플로우 + WiFi 경로 + settingDevice 실패(1005) 시: 서버에 단말 등록 대기 후 등록 완료 처리
    if (
      props.actionType &&
      connectionType.value === 'wifi' &&
      (e?.resultCode === 1005 || e?.resultCode === '1005')
    ) {
      showLocalLoading();
      updateStepStatus(2, 'in_progress', '서버에 단말 등록 여부를 확인하고 있습니다.');

      let pollResult;
      for (let i = 0; i < SettingConfig.requestCount; i++) {
        setLocalLoadingText(
          i === 0
            ? '서버에 단말 등록 여부를 확인하고 있습니다.'
            : `서버에 단말 등록 여부를 ${i + 1}번째 확인하고 있습니다.`,
        );
        updateStepStatus(2, 'in_progress', `서버 등록 확인 중... (${i + 1}번째 시도)`);

        await new Promise((r) => setTimeout(r, SettingConfig.requestInterval));
        pollResult = await getControllerInfo(connectedController.value);

        if (pollResult?.resultCode === '0000' && pollResult?.resultData) {
          updateStepStatus(2, 'completed', '', '', '설정 성공');
          hideLocalLoading();
          const savedWifi = rememberWifiCredentialsIfReady();
          if (connectionSteps.value.length > 3) {
            updateStepStatus(3, 'pending', '', '', '');
            await new Promise((r) => setTimeout(r, 500));
          }
          emit('complete', {
            controller: connectedController.value,
            connectionType: connectionType.value,
            wifiSsid: savedWifi.ssid,
            wifiPassword: savedWifi.password,
          });
          return;
        }
      }

      // 폴링 타임아웃: 실패 처리
      updateStepStatus(2, 'failed', '', '프로비저닝 실패', '프로비저닝 실패: 조명제어기 설정에 실패했습니다. 서버 등록 확인도 실패했습니다.');
      errorMessage.value = '프로비저닝 실패: 조명제어기 설정에 실패했습니다. 서버 등록 확인도 실패했습니다.';
      hideLocalLoading();
      throw e;
    }

    // step[2]의 실패 상태 확인 및 업데이트
    if (connectionSteps.value[2].status !== 'failed') {
      let errorMsg = e?.message || '프로비저닝 실패';
      let errorDetail = `프로비저닝 실패: ${errorMsg}`;

      // 에러 코드별 상세 메시지 처리
      if (e?.resultCode) {
        switch (e.resultCode) {
          case 1002:
            errorDetail = '프로비저닝 실패: 입력한 WiFi를 찾을 수 없습니다. SSID를 확인해주세요.';
            break;
          case 1003:
            errorDetail = '프로비저닝 실패: WiFi 비밀번호가 잘못되었습니다. 비밀번호를 확인해주세요.';
            break;
          case 1004:
            errorDetail = '프로비저닝 실패: 조명제어기와의 연결이 끊어졌습니다. 다시 시도해주세요.';
            break;
          case 1005:
            errorDetail = '프로비저닝 실패: 조명제어기 설정에 실패했습니다.';
            break;
          case 'TIMEOUT':
            errorDetail = '프로비저닝 실패: 응답 시간이 초과되었습니다. 다시 시도해주세요.';
            break;
          default:
            errorDetail = `프로비저닝 실패: ${errorMsg}`;
        }
      }

      updateStepStatus(2, 'failed', '', errorMsg, errorDetail);
      errorMessage.value = errorDetail;
    } else {
      errorMessage.value = e?.message || '프로비저닝에 실패했습니다.';
    }
    throw e;
  } finally {
    isProcessing.value = false;
  }
};

const onWifiSetting = async () => {
  await executeProvisioning();
};

const onOk = async () => {
  // 연결 방식 선택이 필요한 경우 (선택 버튼은 실제로 사용되지 않음 - 선택 카드에서 직접 처리)
  if (showConnectionTypeSelection.value) {
    return;
  }

  // 연결 실패 상태에서 재연결 시도
  if (hasFailedStep.value) {
    // 스캔 단계가 완료된 경우: 스캔 건너뛰고 연결만 재시도
    if (connectionSteps.value[0].status === 'completed' && scannedDevice.value) {
      // 스캔 단계는 completed 상태 유지, 연결 단계만 재시도
      errorMessage.value = '';
      updateStepStatus(1, 'pending', '', ''); // 연결 단계만 초기화
      await startConnection();
      return;
    }
    
    // 스캔 단계가 실패한 경우: 처음부터 다시 시작
    resetSteps();
    scannedDevice.value = null; // 스캔된 장치 정보 초기화
    errorMessage.value = '';
    await startConnection();
    return;
  }

  // 스캔이 완료되지 않았으면 연결 시작
  if (connectionSteps.value[0].status !== 'completed') {
    await startConnection();
    return;
  }

  // 모든 단계 완료 시
  // actionType이 있으면 자동으로 complete 이벤트가 발생하므로 확인창 표시하지 않음
  // actionType이 없으면 기존처럼 확인창 표시
  if (connectionSteps.value[2].status === 'completed') {
    if (!props.actionType) {
      emitter.emit('showPopConfirm', {
        title: '조명제어기 등록',
        description: '조명제어기를 등록하시겠습니까?',
        okCallback: () => {
          const savedWifi = rememberWifiCredentialsIfReady();
          emit('complete', {
            controller: connectedController.value,
            connectionType: connectionType.value,
            wifiSsid: savedWifi.ssid || wifiSsid.value,
            wifiPassword: savedWifi.password || wifiPassword.value,
          });
        },
        cancelCallback: () => {
          // 취소 시 아무 동작 없음
        },
      });
    }
  }
};

const onCancel = () => {
  cleanup();
  emit('cancel');
};

const onBackClick = () => {
  if (!isProcessing.value) {
    onCancel();
  }
};

/** Android 백키: nativeBackRequest 시 다이얼로그가 열려 있으면 "연결 취소할까요?" 확인 팝업 표시.
 *  사용자가 확인 선택 시에만 onCancel() 호출, 취소 선택 시에는 다이얼로그 유지. */
function handleNativeBackRequest() {
  if (!props.visible) return;
  if (typeof window !== 'undefined') {
    window.__nativeBackHandled = true;
  }
  window.flutter_inappwebview?.callHandler?.('backKeyHandled', true);

  emitter.emit('showPopConfirm', {
    title: '연결 취소',
    description: '연결을 취소하시겠습니까?',
    okText: '취소',
    cancelText: '계속',
    okCallback: () => {
      onCancel();
    },
    cancelCallback: () => {
      // 계속 선택 시 팝업만 닫고 다이얼로그는 유지
    },
  });
}

onMounted(() => {
  window.addEventListener('nativeBackRequest', handleNativeBackRequest);
});
</script>

<style scoped>
.dialog-body {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  position: relative;
}

.local-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  /* 바디 영역 안에서만 약한 dim 처리 */
  background: rgba(255, 255, 255, 0);
  pointer-events: none; /* 버튼 클릭 방해하지 않음 (푸터는 원래 바깥) */
}

.local-loading-card {
  width: min(90%, 360px);
  background: rgba(0, 0, 0, 0.5); /* 요청: 투명도 50% */
  color: #ffffff;
  border-radius: 12px;
  padding: 12px 14px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.local-loading-text {
  font-size: 0.85rem;
  line-height: 1.35;
  word-break: keep-all;
}

.step-container {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.step-item {
  position: relative;
  display: flex;
  flex-direction: column;
}

.step-content {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.5rem 0;
}

.step-indicator {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1rem;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.step-pending {
  background-color: #e9ecef;
  color: #6c757d;
  border: 2px solid #dee2e6;
}

.step-in-progress {
  background-color: #0d6efd;
  color: white;
  border: 2px solid #0d6efd;
  animation: pulse 1.5s ease-in-out infinite;
}

.step-completed {
  background-color: #198754;
  color: white;
  border: 2px solid #198754;
}

.step-failed {
  background-color: #dc3545;
  color: white;
  border: 2px solid #dc3545;
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.step-label {
  flex: 1;
  padding-top: 0.5rem;
}

.step-connector {
  position: absolute;
  left: 20px;
  top: 60px;
  width: 2px;
  height: calc(100% - 40px);
  background-color: #dee2e6;
  z-index: 0;
}

.connector-active {
  background-color: #198754;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.step-content-area {
  margin-left: 3rem;
}

.step-details {
  width: 100%;
}

.connection-type-container {
  align-items: stretch;
}

.connection-type-btn {
  min-height: 120px;
  display: flex;
  align-items: stretch;
  flex: 1 1 0;
  min-width: 0;
  width: 0; /* flex: 1 1 0과 함께 사용하여 동일한 크기 보장 */
}

.connection-type-btn > div {
  width: 100%;
}
</style>